import { Button } from '@/components/shadcn/button'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconCirclePlusOutline } from '@icons/IconCirclePlusOutline'
import { IconCollapseChevronOutline } from '@icons/IconCollapseChevronOutline'
import { IconExpandChevronOutline } from '@icons/IconExpandChevronOutline'
import { IconCopyOutline } from '@icons/IconCopyOutline'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconRedoOutline } from '@icons/IconRedoOutline'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { MenuItemForm } from './MenuItemForm'
import { MessagesLandingMenuItem } from './types'
import { PAGE_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { WarningBanner } from '@/components/ui/WarningBanner'
import { SelectOption, SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { CreateCustomMenuDialog } from './CreateCustomMenuDialog'

interface MenuToolbarProps {
  isExpanded: boolean
  hasChanged: boolean
  toggleExpandCollapse: () => void
  addRootItem: (newItem: MessagesLandingMenuItem) => void
  handleSave: () => void
  resetMenuItems: () => void
  selectedMenuName: string
  menuOptions: SelectOption<string>[]
  customMenuNames: string[]
  onMenuSelectionChange: (menuName: string) => void
  onCustomMenuCreated: (menuKey: string, menuName: string) => void
  getCustomMenuApiPathParam: (menuKey: string, menuName?: string) => string
  isCustomMenu: boolean
  onDeleteCustomMenu: () => Promise<boolean>
  isDeletingCustomMenu: boolean
  onCopyMenuLayoutToClipboard: () => void
  onPasteMenuLayoutFromClipboard: () => void
}

export function MenuToolbar({
  isExpanded,
  hasChanged,
  toggleExpandCollapse,
  addRootItem,
  handleSave,
  resetMenuItems,
  selectedMenuName,
  menuOptions,
  customMenuNames,
  onMenuSelectionChange,
  onCustomMenuCreated,
  getCustomMenuApiPathParam,
  isCustomMenu,
  onDeleteCustomMenu,
  isDeletingCustomMenu,
  onCopyMenuLayoutToClipboard,
  onPasteMenuLayoutFromClipboard,
}: MenuToolbarProps) {
  const [isAddMenuItemSheetOpen, setIsAddMenuItemSheetOpen] = useState(false)
  const [isCreateCustomMenuDialogOpen, setIsCreateCustomMenuDialogOpen] = useState(false)
  const [isDeleteCustomMenuDialogOpen, setIsDeleteCustomMenuDialogOpen] = useState(false)

  return (
    <div style={{ top: PAGE_HEADER_HEIGHT }} className="sticky z-20 mb-4 space-y-2 py-2 backdrop-blur">
      <div className="flex space-x-2">
        <TooltipButton tooltip={isExpanded ? 'Collapse all' : 'Expand all'}>
          <Button variant="outline" size="icon" onClick={toggleExpandCollapse}>
            {isExpanded ? <IconCollapseChevronOutline size={14} /> : <IconExpandChevronOutline size={14} />}

            <span className="sr-only">{isExpanded ? 'Collapse all' : 'Expand all'}</span>
          </Button>
        </TooltipButton>

        <SingleSelectCombobox
          className="w-56"
          options={menuOptions}
          selectedValue={selectedMenuName}
          onSelectionChange={(value) => {
            if (value) {
              onMenuSelectionChange(value)
            }
          }}
          placeholder="Select a menu"
          searchPlaceholder="Search menus..."
          emptyMessage="No menus found."
          allowClear={false}
          footerContent={(closePopover) => (
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start"
              onClick={() => {
                closePopover()

                setIsCreateCustomMenuDialogOpen(true)
              }}
            >
              Create new
            </Button>
          )}
        />

        {isCustomMenu ? (
          <ConfirmDialog
            trigger={
              <Button variant="outline" size="icon" aria-label="Delete custom menu">
                <IconDeleteOutline className="size-4" />
              </Button>
            }
            triggerTooltip="Delete custom menu"
            isOpen={isDeleteCustomMenuDialogOpen}
            onOpenChange={setIsDeleteCustomMenuDialogOpen}
            title="Delete custom menu"
            description={`Are you sure you want to delete "${menuOptions.find((o) => o.value === selectedMenuName)?.label ?? selectedMenuName}"? This cannot be undone.`}
            confirmLabel="Delete"
            confirmVariant="destructive"
            closeOnConfirm={false}
            isSubmitting={isDeletingCustomMenu}
            onConfirm={() => {
              void onDeleteCustomMenu().then((deleted) => {
                if (deleted) {
                  setIsDeleteCustomMenuDialogOpen(false)
                }
              })
            }}
          />
        ) : null}

        <TooltipButton tooltip="Copy menu layout JSON to clipboard">
          <Button variant="outline" onClick={() => void onCopyMenuLayoutToClipboard()}>
            <IconCopyOutline className="mr-2 size-4" />
            Copy layout
          </Button>
        </TooltipButton>

        <TooltipButton tooltip="Paste menu layout JSON from clipboard">
          <Button variant="outline" onClick={() => void onPasteMenuLayoutFromClipboard()}>
            Paste from clipboard
          </Button>
        </TooltipButton>

        <div className="flex-grow"></div>

        <SheetWithFormComponent
          title="New menu item"
          triggerTooltip="Add a new top-level menu item"
          trigger={
            <Button variant="outline">
              <IconCirclePlusOutline className="mr-2 size-4" />
              Add menu item
            </Button>
          }
          isOpen={isAddMenuItemSheetOpen}
          onOpenChange={setIsAddMenuItemSheetOpen}
        >
          <MenuItemForm
            showIcon={true}
            onSubmit={(data) => {
              const formData = data as Record<string, unknown>
              const menuItem: MessagesLandingMenuItem = {
                item_type: String(formData.item_type || ''),
                name: String(formData.name || ''),
                page_id: String(formData.page_id || ''),
                description: String(formData.description || ''),
                icon_name: formData.icon_name ? String(formData.icon_name) : undefined,
                roles: (formData.assignedRoles as string[]) ?? [],
                items: [],
              }
              addRootItem(menuItem)
              setIsAddMenuItemSheetOpen(false)
            }}
            initialValues={{ name: '', roles: [], page_id: '', item_type: '', description: '' }}
          />
        </SheetWithFormComponent>

        <Button className="relative overflow-visible" disabled={!hasChanged} onClick={() => handleSave()}>
          Save menu
          {hasChanged && (
            <div className="pointer-events-none absolute inset-[10%] size-[80%] animate-ping rounded-lg border border-primary opacity-50"></div>
          )}
        </Button>

        <TooltipButton tooltip="Undo all changes">
          <Button variant="outline" size="icon" disabled={!hasChanged} onClick={resetMenuItems}>
            <IconRedoOutline size={24} />
          </Button>
        </TooltipButton>
      </div>

      {hasChanged && (
        <WarningBanner
          message="You have unsaved menu changes. Save before navigating away to keep your changes."
          actionLabel="Save menu"
          onAction={handleSave}
        />
      )}

      <CreateCustomMenuDialog
        open={isCreateCustomMenuDialogOpen}
        onOpenChange={setIsCreateCustomMenuDialogOpen}
        existingMenuKeys={customMenuNames}
        getCustomMenuApiPathParam={getCustomMenuApiPathParam}
        onCreated={onCustomMenuCreated}
      />
    </div>
  )
}
